import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowRightFromBracket, faChevronDown, faCircleQuestion, faRocket, faUser } from '@fortawesome/free-solid-svg-icons';
import { TranslocoDirective } from '@jsverse/transloco';
import { isTenantAdmin } from '../../../models/enums/user-role.enum';
import { AuthService } from '../../../services/auth.service';
import { ONBOARDING_TOTAL, OnboardingDataService } from '../../../services/onboarding-data.service';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { HelpPanelService } from '../../help';
import { initials } from '../../utils/initials';
import { BadgeComponent } from '../badge.component';

/** Initials trigger with name, role and Sign out; "Personal details" opens Settings, which only the computer has. */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-account-menu',
  standalone: true,
  imports: [RouterLink, FaIconComponent, TranslocoDirective, CdkMenu, CdkMenuItem, CdkMenuTrigger, BadgeComponent],
  templateUrl: './account-menu.component.html',
})
export class AccountMenuComponent {
  private readonly _authService = inject(AuthService);
  private readonly _help = inject(HelpPanelService);

  protected readonly size = inject(ScreenSizeService).size;
  protected readonly profile = toSignal(this._authService.currentProfile$);
  protected readonly profileFailed = this._authService.profileFailed;
  protected readonly logoutIcon = faArrowRightFromBracket;
  protected readonly chevronIcon = faChevronDown;
  protected readonly accountIcon = faUser;
  protected readonly gettingStartedIcon = faRocket;
  protected readonly helpIcon = faCircleQuestion;
  protected readonly onboarding = inject(OnboardingDataService);
  protected readonly onboardingTotal = ONBOARDING_TOTAL;
  protected readonly menuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
  ];

  protected readonly isOwner = computed(() => isTenantAdmin(this.profile()?.role));

  // The phone has no top bar and no More tab, so its menu carries the entry (computer and tablet have theirs).
  protected readonly showGettingStarted = computed(() => this.size() === 'phone' && this.isOwner() && this.onboarding.openCount() > 0);

  protected readonly initials = computed(() => {
    const profile = this.profile();
    return profile ? initials(`${profile.firstName} ${profile.lastName}`) : '';
  });

  protected openHelp(): void {
    // The menu hands focus back to its trigger as it closes; opening in the same tick would lose the panel's focus to it.
    setTimeout(() => this._help.open());
  }

  protected onLogout(): void {
    this._authService.logout();
  }
}
